import type { OntoPathNode } from '../types'

/**
 * Handling chains for the document-driven Lab demo (#02).
 * Each function returns a synthesised OntoPathNode[] describing what the
 * ontology does once the LLM has extracted the document's entities.
 *
 * Labels are kept in English; structure is consistent with the L3 demo
 * generators so the LabFlow renders the chain the same way.
 */

export function getContractPath(): OntoPathNode[] {
  return [
    {
      id: 'doc-c-party',
      kind: 'object',
      label: 'Party::Acme Pumps Inc.',
      detail: 'Active supplier · 4-year history · 12 prior contracts',
    },
    {
      id: 'doc-c-clauses',
      kind: 'inference',
      label: 'Clause review',
      detail: 'Auto-renewal §12.3 · Force-majeure §17 broad · Cap ¥8.4M / year',
      parents: ['doc-c-party'],
    },
    {
      id: 'doc-c-fm',
      kind: 'constraint',
      label: 'R-LEGAL-FM-04',
      detail: 'Force-majeure clauses must align with template §17.2 (narrow form)',
      citation: 'Legal · Standard FM Clause v2024',
      parents: ['doc-c-clauses'],
    },
    {
      id: 'doc-c-risk',
      kind: 'inference',
      label: 'Risk scoring',
      detail: 'Composite score 62 / 100 (medium) — clause set + party history + cap size',
      parents: ['doc-c-clauses'],
    },
    {
      id: 'doc-c-approval',
      kind: 'candidate',
      label: 'Approval route',
      detail: 'Score > 50 → VP signing required · multi-year → board notify',
      parents: ['doc-c-fm', 'doc-c-risk'],
    },
    {
      id: 'doc-c-act',
      kind: 'action',
      label: 'reviseClause + escalate',
      detail: 'Send §17 redraft to legal · route to VP · CC procurement · audit-track',
      parents: ['doc-c-approval'],
    },
  ]
}

export function getRfqPath(): OntoPathNode[] {
  return [
    {
      id: 'doc-r-rfq',
      kind: 'object',
      label: 'RFQ::29117',
      detail: 'Pump P-1023 mechanical seal · 3 bidders · 14-day SLA',
    },
    {
      id: 'doc-r-equipment',
      kind: 'object',
      label: 'Equipment::P-1023',
      detail: 'Sour service · H₂S 12.6% · Cl⁻ 18,300 mg/L · VP-ratio 0.42',
      parents: ['doc-r-rfq'],
    },
    {
      id: 'doc-r-cert',
      kind: 'inference',
      label: 'Cert version check',
      detail: 'C bid cites NACE 2024-08 · current standard is 2025-03',
      parents: ['doc-r-rfq'],
    },
    {
      id: 'doc-r-c-mat',
      kind: 'constraint',
      label: 'R-MAT-NACE-21',
      detail: 'Sour service + high Cl⁻ → SS316 forbidden',
      citation: 'NACE / ISO 15156-3',
      parents: ['doc-r-equipment'],
    },
    {
      id: 'doc-r-c-cert',
      kind: 'constraint',
      label: 'R-CERT-CURRENT-02',
      detail: 'NACE cert must be current version',
      citation: 'Group QA-CERT-2024',
      parents: ['doc-r-cert'],
    },
    {
      id: 'doc-r-cand',
      kind: 'candidate',
      label: 'Compliance matrix',
      detail: 'A: violates plan · B: violates material · C: cert outdated',
      parents: ['doc-r-c-mat', 'doc-r-c-cert'],
    },
    {
      id: 'doc-r-act',
      kind: 'action',
      label: 'expandRfq + raiseMOC',
      detail: 'Invite D supplier · file MOC-Fast-Track · intensify P-1023B inspection',
      parents: ['doc-r-cand'],
    },
  ]
}

export function getIncidentPath(): OntoPathNode[] {
  return [
    {
      id: 'doc-i-asset',
      kind: 'object',
      label: 'Asset::P-1023A',
      detail: 'Sour-service feed pump · last overhaul 2024-09 · standby of P-1023B',
    },
    {
      id: 'doc-i-severity',
      kind: 'inference',
      label: 'Severity assessment',
      detail: 'Stage-1 leak · contained · no HSE exposure · class B incident',
      parents: ['doc-i-asset'],
    },
    {
      id: 'doc-i-root',
      kind: 'inference',
      label: 'Root-cause hypotheses',
      detail: '1) corrosion accelerated by recent H₂S spike  2) seal-face wear from extended duty',
      parents: ['doc-i-asset'],
    },
    {
      id: 'doc-i-c-moc',
      kind: 'constraint',
      label: 'R-MOC-INCIDENT-02',
      detail: 'Stage-1 leak on sour service → MOC required within 48 h',
      citation: 'Group HSE-MOC-2024',
      parents: ['doc-i-severity'],
    },
    {
      id: 'doc-i-c-spare',
      kind: 'constraint',
      label: 'R-SPARE-ETA-09',
      detail: 'Sour-service spare must arrive < remaining life of incident asset',
      citation: 'Group EQ-SPARE',
      parents: ['doc-i-root'],
    },
    {
      id: 'doc-i-plan',
      kind: 'candidate',
      label: 'Remediation plan set',
      detail: 'A: expedited seal replacement (D supplier · 14 d)  B: load reduction + daily inspection',
      parents: ['doc-i-c-moc', 'doc-i-c-spare'],
    },
    {
      id: 'doc-i-act',
      kind: 'action',
      label: 'expediteRfq + raiseMOC + alert',
      detail: 'Expedite RFQ for replacement · file MOC · intensified daily inspection · notify HSE',
      parents: ['doc-i-plan'],
    },
  ]
}
